import { useMemo, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { SButton, SEmptyState, SText } from '@setvi/shared/components';
import {
  ProductPriceType,
  Product as ProductType
} from '@setvi/shared/interfaces';
import { distances } from '@setvi/shared/styles';
import { EmailIconV2 } from '@setvi/shared/components/sicons';

import { Customer } from 'Services/MongoRealm/Types';
import { Product } from './product';
import { FlexBox, StyledSearch } from './styled';

const useStyles = makeStyles({
  container: {
    width: 360,
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  searchContainer: {
    padding: distances.px.base
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
    overflow: 'hidden'
  }
});

interface ProductDrawerContentProps {
  products: ProductType[];
  buttonLabel: string;
  priceType: ProductPriceType;
  selectedCustomer: Customer;
  priceLoading?: boolean;
  handleRemoveProduct: (product: ProductType) => void;
  getProductPrice: (product: ProductType[]) => Promise<void>;
  handleEmail: () => void;
  handleClose: () => void;
  clearAll?: () => void;
}

export const handleStaticSearchProducts = (
  products: ProductType[],
  search: string
) => {
  if (!search.trim().length) return products;

  return products.filter(
    i =>
      i?.name.toLowerCase()?.includes(search.toLowerCase()) ||
      i?.sku.toLowerCase()?.includes(search.toLowerCase()) ||
      (i?.vendor?.name || '').toLowerCase()?.includes(search.toLowerCase()) ||
      (i?.manufacturerSku || '').toLowerCase().includes(search?.toLowerCase())
  );
};

export const ProductDrawerContent = ({
  products,
  buttonLabel,
  priceType,
  selectedCustomer,
  priceLoading,
  handleEmail,
  handleClose,
  handleRemoveProduct,
  getProductPrice,
  clearAll
}: ProductDrawerContentProps) => {
  const [search, setSearch] = useState('');
  const classes = useStyles();

  const list = useMemo(
    () => handleStaticSearchProducts(products, search),
    [products, search]
  );

  return (
    <Box className={classes.container}>
      <FlexBox p={3}>
        <SText
          weight="bold"
          title={`${products.length} Product${products.length > 1 ? 's' : ''} Selected`}
        />
        {clearAll && (
          <SButton
            variant="text"
            onClick={clearAll}
            style={{
              fontSize: 12
            }}
            startIcon={
              <CloseRoundedIcon
                style={{
                  width: 14,
                  height: 14
                }}
              />
            }>
            Clear All
          </SButton>
        )}
      </FlexBox>

      <Box className={classes.searchContainer}>
        <StyledSearch
          defaultValue={search}
          onChange={val => setSearch(val)}
          placeholder="Search selected products..."
          width="100%"
          searchIconSize="14px"
        />
      </Box>
      <Box className={classes.content}>
        <Box
          overflow="auto"
          height="100%"
          p="12px"
          display="flex"
          flexDirection="column"
          gridGap="12px">
          {list.length > 0 ? (
            list.map(product => (
              <Product
                key={product._id}
                product={product}
                searchQuery={search}
                priceType={priceType}
                priceLoading={priceLoading}
                selectedCustomer={selectedCustomer}
                removeProduct={handleRemoveProduct}
                getProductPrice={getProductPrice}
              />
            ))
          ) : (
            <Box
              display="flex"
              height="100%"
              alignItems="center"
              justifyContent="center">
              <SEmptyState
                additionalContent={
                  <SText
                    noLineClamp
                    weight="semibold"
                    title="No Results Found"
                    style={{
                      marginTop: 24
                    }}
                  />
                }
                imageSrc="/images/empty/no-results.png"
              />
            </Box>
          )}
        </Box>

        <FlexBox>
          <SButton
            fullWidth
            variant="outlined"
            color="default"
            onClick={handleClose}>
            Close
          </SButton>

          <SButton
            fullWidth
            onClick={handleEmail}
            startIcon={<EmailIconV2 fill="fff" />}>
            {buttonLabel}
          </SButton>
        </FlexBox>
      </Box>
    </Box>
  );
};
